import { isNativeApp } from './config';
import { translations } from './i18n/translations';
import { hasWebPushSubscription } from './webPush';
import { shouldSuppressOsNotification } from './notifyFocus';
import {
  showLocalNotification,
  cancelCallNotification
} from './native/notifications';

function messageCopy(message) {
  const name = message?.sender_name || message?.name || '';
  const preview =
    message?.type === 'image'
      ? translations.imageMessage
      : message?.type === 'video'
        ? translations.videoMessage
        : String(message?.content || translations.newMessage).slice(0, 80);
  return {
    title: translations.newMessageTitle,
    body: name ? `${name}: ${preview}` : preview
  };
}

function callCopy(data) {
  const name = data?.callerName || data?.caller_name || '';
  const video = data?.type === 'video' || data?.callType === 'video';
  return {
    title: translations.incomingCallTitle,
    body: name
      ? `${name} ${video ? translations.incomingVideo : translations.incomingAudio} می‌گیرد`
      : video
        ? translations.incomingVideo
        : translations.incomingCall
  };
}

function showWebNotification({ title, body, tag, data, requireInteraction }) {
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  if (!('Notification' in window) || Notification.permission !== 'granted') return false;
  try {
    const notification = new Notification(title, {
      body,
      tag,
      dir: 'rtl',
      lang: 'fa',
      icon: '/icon-192.png?v=6',
      data,
      requireInteraction: Boolean(requireInteraction)
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
}

export async function dispatchIncomingCall(data, currentUserId) {
  if (!data || data.callerId === currentUserId) return { os: false, inApp: false };
  if (shouldSuppressOsNotification({ type: 'incoming-call', callId: data.id })) {
    return { os: false, inApp: false };
  }

  const copy = callCopy(data);
  const extra = {
    type: 'incoming-call',
    callId: data.id,
    callerId: data.callerId,
    callType: data.type || data.callType || 'audio'
  };

  if (isNativeApp()) {
    await showLocalNotification({
      title: copy.title,
      body: copy.body,
      extra,
      channelId: 'ashiiane-calls',
      idSeed: `call-${data.id}`
    });
    return { os: true, inApp: false };
  }

  if (hasWebPushSubscription()) {
    return { os: true, inApp: false };
  }

  const os = showWebNotification({
    title: copy.title,
    body: copy.body,
    tag: `call-${data.id}`,
    data: extra,
    requireInteraction: true
  });
  return { os, inApp: !os, ...copy };
}

export async function dispatchNewMessage(message, currentUserId) {
  if (!message || message.sender_id === currentUserId) return { os: false, inApp: false };
  if (
    shouldSuppressOsNotification({
      type: 'new-message',
      conversationId: message.conversationId
    })
  ) {
    return { os: false, inApp: false };
  }

  const copy = messageCopy(message);
  const extra = {
    type: 'new-message',
    conversationId: message.conversationId
  };

  if (isNativeApp()) {
    await showLocalNotification({
      title: copy.title,
      body: copy.body,
      extra,
      channelId: 'ashiiane-messages',
      idSeed: `msg-${message.conversationId || message.id}`
    });
    return { os: true, inApp: false };
  }

  if (hasWebPushSubscription()) {
    return { os: true, inApp: false };
  }

  const os = showWebNotification({
    title: copy.title,
    body: copy.body,
    tag: `msg-${message.conversationId || 'ashiiane'}`,
    data: extra
  });
  return { os, inApp: !os, ...copy };
}

export async function dismissIncomingCallNotice(callId) {
  if (callId) await cancelCallNotification(callId);
}
